import { useState } from "react";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { submitDiagnostic, errorText } from "@/lib/api";
import { trackConversion } from "@/lib/track";
import { PhoneVerify } from "@/components/PhoneVerify";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[13px] font-semibold text-slate-700">{label}</span>
    {children}
  </label>
);
const inputCls = "mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-3 text-[15px] outline-none transition-colors focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100";

const savedContact = () => {
  try { return JSON.parse(localStorage.getItem("aor_contact") || "{}") || {}; } catch { return {}; }
};

export const ContactStep = ({ answers, onDone }) => {
  const [f, setF] = useState(() => ({ name: savedContact().name || "", email: savedContact().email || "", consent_result: false, consent_marketing: false }));
  const [ph, setPh] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_result) return toast.error("Please agree to receive your result by email.");
    if (!ph) return toast.error("Please verify your mobile number first.");
    setBusy(true);
    try {
      const { industry, problem_idea, first_result, ...req } = answers;
      const res = await submitDiagnostic({
        answers: { ...req, industry: industry || null, problem_idea: problem_idea || null, first_result: first_result || null },
        name: f.name, email: f.email, phone: ph.phone, phone_token: ph.token,
        consent_result: f.consent_result, consent_marketing: f.consent_marketing,
      });
      trackConversion("diagnostic", { transaction_id: `diagnostic-${res.id}` });
      toast.success("Your starting point is ready. We have emailed your PDF guide.");
      onDone(res);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="quiz-contact-form" onSubmit={submit} className="space-y-4">
      <div>
        <h3 className="font-display text-[22px] font-bold leading-tight text-[#0B1B33] md:text-[26px]">Get your result and PDF guide</h3>
        <p className="mt-2 text-[14px] text-slate-600">We use your email only to send your diagnostic result and your guide, <i>Your AI Project Starting Point</i>.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Your name"><input data-testid="input-name" required value={f.name} onChange={(e) => set("name")(e.target.value)} className={inputCls} /></Field>
        <Field label="Email"><input data-testid="input-email" type="email" required value={f.email} onChange={(e) => set("email")(e.target.value)} className={inputCls} /></Field>
      </div>
      <PhoneVerify value={ph} onChange={setPh} testId="diag-phone" />
      <div className="space-y-3 rounded-xl bg-slate-50 p-4">
        <label className="flex gap-3 text-[14px] text-slate-800">
          <Checkbox data-testid="consent-result-checkbox" checked={f.consent_result} onCheckedChange={(v) => set("consent_result")(!!v)} className="mt-0.5 data-[state=checked]:bg-[#0E9384] data-[state=checked]:border-[#0E9384]" />
          Send me my diagnostic result and PDF guide by email. (Required)
        </label>
        <label className="flex gap-3 text-[14px] text-slate-800">
          <Checkbox data-testid="consent-marketing-checkbox" checked={f.consent_marketing} onCheckedChange={(v) => set("consent_marketing")(!!v)} className="mt-0.5 data-[state=checked]:bg-[#0E9384] data-[state=checked]:border-[#0E9384]" />
          I would also like occasional ArtOfResume cohort updates. (Optional, unsubscribe anytime)
        </label>
      </div>
      <button data-testid="quiz-submit-btn" disabled={busy} className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-4 text-[17px] font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Show My Starting Point <ArrowRight className="arrow h-5 w-5" /></>}
      </button>
      <p className="flex gap-2 text-[12.5px] text-slate-600">
        <Lock className="h-4 w-4 shrink-0" /> Your answers are private and used only to tailor guidance. They are never sold or shared.
      </p>
    </form>
  );
};
